import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

import { db } from '../services/firebase.js';

/**
 * Upload ChapterLocations data to Firestore
 * 
 * @param {Object} data - The nested location data (e.g., { level_2: { chapter_1: { ... } } })
 * @returns {Promise<Object>} Result object
 */
export const uploadChapterLocations = async (data) => {
    try {
        console.group('📤 Uploading ChapterLocations');
        const results = [];

        // Iterate through Levels
        // data structure: { level_2: { chapter_1: { ... } } }
        for (const [levelKey, levelData] of Object.entries(data)) {
            // levelKey = "level_2"

            // Iterate through Chapters
            for (const [chapterKey, chapterData] of Object.entries(levelData)) {
                // chapterKey = "chapter_1"

                // Construct Document ID: "level_2_chapter_1"
                const docId = `${levelKey}_${chapterKey}`;
                const docRef = doc(db, 'feature_testing/Korea_University/ChapterLocations', docId);

                // Add Metadata
                const payload = {
                    ...chapterData, // Contains "locations": [...]
                    levelId: levelKey,
                    chapterId: chapterKey,
                    lastUpdated: serverTimestamp(),
                    last_updated_by: 'script_upload',
                    uploaded_at: new Date().toISOString()
                };

                console.log(`Processing ${docId}...`);

                // Upload
                await setDoc(docRef, payload, { merge: true });
                results.push({ id: docId, success: true });
            }
        }

        console.log('✅ All Uploads Successful');
        console.groupEnd();

        return {
            success: true,
            results: results
        };

    } catch (error) {
        console.error('❌ Upload failed:', error);
        console.groupEnd();
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Upload and Verify
 */
export const uploadAndVerifyChapterLocations = async () => {
    try {
        console.group('🔄 Upload and Verify ChapterLocations');

        // Import default data
        const { default: defaultData } = await import('../data/chapterLocationsData.json');

        // Upload
        const uploadResult = await uploadChapterLocations(defaultData);

        if (!uploadResult.success) {
            throw new Error(uploadResult.error);
        }

        // Verify one of them (e.g., level_2_chapter_1)
        const verifyId = 'level_2_chapter_1';
        const docRef = doc(db, 'feature_testing/Korea_University/ChapterLocations', verifyId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            console.log(`✅ Verification successful: ${verifyId} exists`);
            console.log('Data:', docSnap.data());
            console.groupEnd();
            return {
                success: true,
                data: docSnap.data()
            };
        } else {
            console.warn(`⚠️ Verification: Document ${verifyId} not found (maybe not in data?)`);
            console.groupEnd();
            // Still return true if upload reported success, just warn
            return { success: true, verified: false };
        }

    } catch (error) {
        console.error('❌ Upload and verify failed:', error);
        console.groupEnd();
        return {
            success: false,
            error: error.message
        };
    }
};
